import { BadgePercent, ExternalLink, Lock, LifeBuoy } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useFeature, upgradeHintFor } from '../../lib/features';

// Launchpad affiliate/offer URL wired on the JV page — "request your link" destination.
const AFFILIATE_URL = 'https://launchpadjv.com/offer/top-offer/349';
// TODO: confirm the real support destination (help-desk URL or address).
const SUPPORT_HREF = 'mailto:support@pixalab.ai';
// Reseller requests are processed only after the launch closes.
const LAUNCH_END = 'June 12th, 2026';

// Reseller Rights — Enterprise-only activation instructions (distinct from the
// reseller management console). Gated on the `resellerLicensing` entitlement.
export default function ResellerRightsTab() {
  const unlocked = useFeature('resellerLicensing');

  if (!unlocked) {
    return (
      <div className="space-y-5">
        <PageHeader
          variant="gradient"
          icon={BadgePercent}
          eyebrow="Enterprise"
          title="Reseller Rights"
          description="Resell RankOnAI and earn commission on every sale through your affiliate link."
        />
        <div className="card text-center py-10">
          <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center">
            <Lock className="w-7 h-7" />
          </div>
          <h2 className="text-lg font-semibold">Reseller rights are an Enterprise feature</h2>
          <p className="text-sm text-gray-600 mt-2 max-w-md mx-auto">
            Upgrade to {upgradeHintFor('resellerLicensing')} to unlock reseller rights and earn a
            100% commission across the entire RankOnAI funnel.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        variant="gradient"
        icon={BadgePercent}
        eyebrow="Enterprise"
        title="Reseller Rights"
        description="Resell RankOnAI and earn a 100% commission on every sale through your affiliate link."
      />

      <div className="card">
        <p className="text-sm text-ink-700 mb-5">
          Activating your reseller rights takes two quick steps:
        </p>

        <ol className="space-y-4">
          <li className="flex gap-3">
            <span className="flex-none h-7 w-7 rounded-full bg-brand-50 text-brand-700 font-semibold text-sm flex items-center justify-center">
              1
            </span>
            <div className="text-sm text-ink-700">
              To get your reseller request approved, first sign up on Launchpad as an affiliate.
              Once signed up, request your RankOnAI affiliate link{' '}
              <a
                href={AFFILIATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium text-brand-600 hover:underline"
              >
                Here <ExternalLink className="w-3.5 h-3.5" />
              </a>
              .
            </div>
          </li>

          <li className="flex gap-3">
            <span className="flex-none h-7 w-7 rounded-full bg-brand-50 text-brand-700 font-semibold text-sm flex items-center justify-center">
              2
            </span>
            <div className="text-sm text-ink-700">
              After that, share your <strong>Affiliate ID</strong> and your{' '}
              <strong>registered email</strong> with our support desk{' '}
              <a
                href={SUPPORT_HREF}
                className="inline-flex items-center gap-1 font-medium text-brand-600 hover:underline"
              >
                Here <LifeBuoy className="w-3.5 h-3.5" />
              </a>
              .
            </div>
          </li>
        </ol>

        <div className="mt-6 rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
            <BadgePercent className="w-4 h-4" />
            100% commission
          </div>
          <p className="text-sm text-emerald-900/80 mt-1">
            Once approved, you'll earn a 100% commission on every sale made through your affiliate
            link — across the entire RankOnAI funnel.
          </p>
        </div>

        <p className="text-xs text-ink-500 mt-4">
          Reseller requests will be approved/processed after the launch ends (the launch concludes
          on <strong>{LAUNCH_END}</strong>).
        </p>
      </div>
    </div>
  );
}
